// Aeronet Knowledge rebuild (docs/design/internal-knowledge/04).
// Customer-page anatomy; Library + Who-are-we tabs; hardcoded DEPTS/wiki killed.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, Button, Tab, Tabs } from "@mui/material";
import {
  getAeronetOverview,
  listDepartmentsApi,
  listProjectsPublic,
  customerActivity,
  deptActivity,
  listFiles,
  uploadFile,
  createFolder,
  presignFile,
  getPresence,
  type AeronetOverview,
  type DepartmentRow,
  type Project,
  type StoreTags,
  type StoreObject,
  type PresenceSession,
} from "../../lib/brain-api";
import type { Thought } from "../../lib/brain-types";
import { useProfile } from "../../lib/profile";
import { useAgent } from "../../agent/AgentProvider";
import { Explain } from "../../agent/Explain";
import { brainTokens } from "../../design/brainTheme";
import {
  PageHeader,
  SectionCard,
  StatusPill,
  EmptyState,
  Icon,
  KpiCard,
  PersonAvatar,
  CustomerAvatar,
} from "../../mui";
import { fmtBytes } from "../../mui/format";
import { LibraryTab } from "./library/LibraryTab";
import { WhoAreWeTab } from "./people/WhoAreWeTab";
import { CompanySkillsTab } from "./CompanySkillsTab";
import { ScopeDocsTab } from "./docs/ScopeDocsTab";

const c = brainTokens.color;

type TabKey = "overview" | "docs" | "departments" | "projects" | "library" | "skills" | "who" | "files" | "activity";

const ICON_FALLBACK: Record<string, string> = {
  code: "hex",
  "server-cog": "settings",
  bulb: "info",
  headset: "help",
  "chart-arrows": "chart",
  "currency-dollar": "dollar",
};
function glyph(name?: string): string {
  if (!name) return "hex";
  return ICON_FALLBACK[name] ?? name;
}

export function AeronetKnowledgePageMui() {
  const [params, setParams] = useSearchParams();
  const dept = params.get("dept");
  const setDept = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("dept", id);
    else next.delete("dept");
    setParams(next, { replace: true });
  };

  return dept ? (
    <DepartmentDetail dept={dept} onBack={() => setDept(null)} />
  ) : (
    <AeronetIndex onOpenDept={(id) => setDept(id)} />
  );
}

function AeronetIndex({ onOpenDept }: { onOpenDept: (id: string) => void }) {
  const [params, setParams] = useSearchParams();
  const tab = ((params.get("tab") as TabKey) || "overview") as TabKey;
  const docKey = params.get("doc");
  const goTab = (t: TabKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    if (t !== "library") next.delete("doc");
    setParams(next, { replace: true });
  };

  const { setPageContext } = useAgent();
  const { enqueueSnackbar } = useSnackbar();
  const [overview, setOverview] = useState<AeronetOverview | null>(null);
  const [depts, setDepts] = useState<DepartmentRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessions, setSessions] = useState<PresenceSession[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ov, d, p, presence] = await Promise.all([
        getAeronetOverview(),
        listDepartmentsApi(),
        listProjectsPublic(),
        getPresence(),
      ]);
      setOverview(ov);
      setDepts(d);
      // Show the same non-archived set the Projects page shows (not just active/planned).
      setProjects(p.filter((x) => x.status !== "archived"));
      setSessions(presence.sessions);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load Aeronet", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void getPresence().then((p) => setSessions(p.sessions)).catch(() => undefined);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setPageContext({ route: "/aeronet", entity: { type: "customer", id: "aeronet", name: "Aeronet" }, tab });
    return () => setPageContext(null);
  }, [tab, setPageContext]);

  const deptLabels = useMemo(
    () => Object.fromEntries(depts.map((d) => [d.deptId, d.label])),
    [depts]
  );

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "overview", label: "Overview" },
    { key: "docs", label: "Docs" },
    { key: "departments", label: `Departments (${depts.length})` },
    { key: "projects", label: `Projects (${projects.length})` },
    { key: "library", label: "Library" },
    { key: "skills", label: "Skills" },
    { key: "who", label: "Who are we" },
    { key: "files", label: "Files" },
    { key: "activity", label: "Activity" },
  ];

  if (loading || !overview) {
    return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading Aeronet…</Box>;
  }

  const k = overview.kpis;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: "18px", flexWrap: "wrap" }}>
        <CustomerAvatar name="Aeronet" size={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            <PageHeader title="Aeronet" />
            <StatusPill tone="warning" label="INTERNAL" />
          </Box>
          <Box sx={{ fontSize: 13, color: c.muted2, mt: 0.25 }}>
            Us, as a customer of ourselves · {k.people} people · {k.departments} departments
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", mb: 2.5 }}>
        <KpiCard label="People" value={<Explain snapshot={{ label: "People", value: k.people }}>{k.people}</Explain>} />
        <KpiCard label="Departments" value={k.departments} />
        <KpiCard label="Active projects" value={k.activeProjects} />
        <KpiCard
          label="Library docs"
          value={k.libraryDocs}
          hint={`${k.freshness.fresh} fresh · ${k.freshness.aging} aging · ${k.freshness.stale} stale`}
        />
        <KpiCard label="Captured this week" value={k.capturedThisWeek} />
      </Box>

      <Tabs value={tab} onChange={(_, v) => goTab(v as TabKey)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3 }}>
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} disableRipple />
        ))}
      </Tabs>

      {tab === "overview" && (
        <OverviewPane overview={overview} sessions={sessions} onOpenLibraryDoc={(key) => {
          const next = new URLSearchParams(params);
          next.set("tab", "library");
          next.set("doc", key);
          setParams(next, { replace: true });
        }} />
      )}
      {tab === "docs" && (
        <ScopeDocsTab
          prefix="customers/aeronet/"
          label="Aeronet"
          openKey={params.get("scopedoc")}
          onOpenKey={(rel) => {
            const next = new URLSearchParams(params);
            if (rel) next.set("scopedoc", rel);
            else next.delete("scopedoc");
            setParams(next, { replace: true });
          }}
        />
      )}
      {tab === "departments" && (
        <DepartmentsGrid depts={depts} onOpen={onOpenDept} />
      )}
      {tab === "projects" && <ProjectsPane projects={projects} depts={depts} />}
      {tab === "library" && (
        <LibraryTab
          openDocKey={docKey}
          onOpenDoc={(key) => {
            const next = new URLSearchParams(params);
            next.set("tab", "library");
            if (key) next.set("doc", key);
            else next.delete("doc");
            setParams(next, { replace: true });
          }}
          deptLabels={deptLabels}
        />
      )}
      {tab === "skills" && <CompanySkillsTab />}
      {tab === "who" && <WhoAreWeTab />}
      {tab === "files" && (
        <FilesPanelMui prefix="customers/aeronet/" tags={{ customer: "aeronet", source: "upload", tier: "company" }} />
      )}
      {tab === "activity" && (
        <ActivityFeedMui load={() => customerActivity("aeronet")} emptyHint="Nothing tagged to Aeronet yet." />
      )}
    </Box>
  );
}

function OverviewPane({
  overview,
  sessions,
  onOpenLibraryDoc,
}: {
  overview: AeronetOverview;
  sessions: PresenceSession[];
  onOpenLibraryDoc: (key: string) => void;
}) {
  const digest = overview.digest;
  const live = sessions.filter((s) => s.state === "active" || s.state === "held-unpushed");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <SectionCard label="The week, written by the brain">
        {digest.empty ? (
          <Box sx={{ fontSize: 14.5, color: c.ink3, fontStyle: "italic", lineHeight: 1.55 }}>
            {digest.emptyMessage ||
              "Quiet week. Either we shipped nothing or we forgot to tell the brain — probably the second."}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(digest.departments ?? []).map((d) =>
              d.bullets?.length ? (
                <Box key={d.deptId}>
                  <Box sx={{ fontWeight: 600, color: c.ink, mb: 0.75 }}>{d.label ?? d.deptId}</Box>
                  {d.bullets.map((b, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1, py: 0.4, fontSize: 13.5, color: c.ink3 }}>
                      <StatusPill
                        tone={b.tag === "landmine" ? "warning" : b.tag === "decision" ? "teal" : "success"}
                        label={b.tag}
                      />
                      <Box>{b.text}</Box>
                    </Box>
                  ))}
                </Box>
              ) : null
            )}
          </Box>
        )}
        <Box sx={{ mt: 1.5, fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.faint }}>
          {digest.byline || "compiled by the brain · Monday 6am"}
        </Box>
      </SectionCard>

      <SectionCard label="Company canon">
        {overview.canon.length === 0 ? (
          <Box sx={{ fontSize: 13, color: c.muted2 }}>Pin canon docs in the Library to surface them here.</Box>
        ) : (
          overview.canon.map((item) => (
            <Box key={item.key} sx={{ py: 1.25, borderTop: `1px solid ${c.borderLight}`, "&:first-of-type": { borderTop: "none" } }}>
              <Box sx={{ fontWeight: 600, color: c.ink, mb: 0.35 }}>{item.title}</Box>
              <Box sx={{ fontSize: 13.5, color: c.ink3, mb: 0.5 }}>{item.summary || "—"}</Box>
              <Box
                component="button"
                onClick={() => onOpenLibraryDoc(item.key)}
                sx={{ border: "none", background: "none", p: 0, color: c.tealInk, fontSize: 13, cursor: "pointer", fontFamily: brainTokens.font.sans }}
              >
                read more in Library →
              </Box>
            </Box>
          ))
        )}
      </SectionCard>

      <SectionCard label={`Right now — ${live.length} active session${live.length === 1 ? "" : "s"}`}>
        {live.length === 0 ? (
          <Box sx={{ fontSize: 13, color: c.muted2 }}>Nobody checked out right now.</Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {live.map((s) => (
              <Box
                key={`${s.scope}|${s.holder}`}
                component={RouterLink}
                to={s.slug ? `/people/${s.slug}` : "/aeronet?tab=who"}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", color: c.ink }}
              >
                <PersonAvatar name={s.displayName || s.holder} size={28} presence={s.state} />
                <Box sx={{ fontSize: 13 }}>{s.displayName || s.holder}</Box>
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}

function DepartmentsGrid({ depts, onOpen }: { depts: DepartmentRow[]; onOpen: (id: string) => void }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
      {depts.map((d) => (
        <Box
          key={d.deptId}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(d.deptId)}
          onKeyDown={(e) => e.key === "Enter" && onOpen(d.deptId)}
          sx={{
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.panel}px`,
            p: 2,
            cursor: "pointer",
            "&:hover": { borderColor: c.hoverBorder },
          }}
        >
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mb: 1 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 1, background: c.borderLight3, display: "grid", placeItems: "center", color: c.muted2 }}>
              <Icon name={glyph(d.icon)} size={18} />
            </Box>
            <Box>
              <Box sx={{ fontWeight: 600, color: c.ink }}>{d.label}</Box>
              <Box sx={{ fontSize: 12, color: c.muted2 }}>{d.members?.length ?? 0} members</Box>
            </Box>
          </Box>
          <Box sx={{ fontSize: 12.5, color: c.muted2 }}>
            {d.activeProjectCount ?? 0} projects · {d.libraryDocCount ?? 0} docs
            {d.lead ? ` · lead ${d.lead}` : ""}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ProjectsPane({ projects, depts }: { projects: Project[]; depts: DepartmentRow[] }) {
  const [team, setTeam] = useState<string>("");
  const filtered = team ? projects.filter((p) => p.team === team) : projects;
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Button size="small" variant={team === "" ? "contained" : "outlined"} onClick={() => setTeam("")}>
          All
        </Button>
        {depts.map((d) => (
          <Button key={d.deptId} size="small" variant={team === d.deptId ? "contained" : "outlined"} onClick={() => setTeam(d.deptId)}>
            {d.label}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" component={RouterLink} to="/projects">
          New project →
        </Button>
      </Box>
      {filtered.length === 0 ? (
        <EmptyState icon="folder" title="No projects">Active projects for this filter will show up here.</EmptyState>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
          {filtered.map((p) => (
            <Box
              key={p.projectId}
              component={RouterLink}
              to={`/projects/${p.projectId}`}
              sx={{
                textDecoration: "none",
                border: `1px solid ${c.border}`,
                borderRadius: `${brainTokens.radius.panel}px`,
                p: 2,
                "&:hover": { borderColor: c.hoverBorder },
              }}
            >
              <Box sx={{ fontWeight: 600, color: c.ink }}>{p.name}</Box>
              <Box sx={{ fontSize: 12.5, color: c.muted2, mt: 0.5 }}>
                {p.status} · {p.team || "—"} · {p.owner || "no owner"}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function DepartmentDetail({ dept, onBack }: { dept: string; onBack: () => void }) {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as string) || "overview";
  const [meta, setMeta] = useState<DepartmentRow | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    void Promise.all([listDepartmentsApi(), listProjectsPublic({ team: dept })]).then(([all, p]) => {
      setMeta(all.find((d) => d.deptId === dept) ?? null);
      setProjects(p);
    });
  }, [dept]);

  const go = (t: string) => {
    const next = new URLSearchParams(params);
    next.set("dept", dept);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <Box>
      <Box component="button" onClick={onBack} sx={{ border: "none", background: "none", color: c.tealInk, cursor: "pointer", mb: 2, p: 0, fontFamily: brainTokens.font.sans, fontSize: 13 }}>
        ← All departments
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 1, background: c.borderLight3, display: "grid", placeItems: "center" }}>
          <Icon name={glyph(meta?.icon)} size={20} />
        </Box>
        <Box>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: c.ink }}>{meta?.label ?? dept}</Box>
          <Box sx={{ fontSize: 13, color: c.muted2 }}>{meta?.descr}</Box>
        </Box>
      </Box>
      <Tabs value={tab} onChange={(_, v) => go(v)} sx={{ mb: 2.5 }}>
        {["overview", "library", "projects", "people", "activity"].map((t) => (
          <Tab key={t} value={t} label={t[0]!.toUpperCase() + t.slice(1)} disableRipple />
        ))}
      </Tabs>
      {tab === "overview" && (
        <ActivityFeedMui load={() => deptActivity(dept)} emptyHint="No recent activity for this department." />
      )}
      {tab === "library" && (
        <LibraryTab
          scopeFilter={`dept:${dept}`}
          onOpenDoc={(key) => {
            const next = new URLSearchParams(params);
            next.set("dept", dept);
            next.set("tab", "library");
            if (key) next.set("doc", key);
            else next.delete("doc");
            setParams(next, { replace: true });
          }}
          openDocKey={params.get("doc")}
          deptLabels={meta ? { [meta.deptId]: meta.label } : {}}
        />
      )}
      {tab === "projects" && <ProjectsPane projects={projects} depts={meta ? [meta] : []} />}
      {tab === "people" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {(meta?.members ?? []).map((m) => (
            <Box
              key={m.userId}
              component={RouterLink}
              to={m.slug ? `/people/${m.slug}` : "/aeronet?tab=who"}
              sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none", color: c.ink, border: `1px solid ${c.border}`, borderRadius: 1, px: 1.25, py: 0.75 }}
            >
              <PersonAvatar name={m.displayName} size={28} />
              {m.displayName}
              {meta?.lead === m.slug && <StatusPill tone="teal" label="lead" />}
            </Box>
          ))}
          {(meta?.members ?? []).length === 0 && <Box sx={{ color: c.muted2, fontSize: 13 }}>No members yet.</Box>}
        </Box>
      )}
      {tab === "activity" && (
        <ActivityFeedMui load={() => deptActivity(dept)} />
      )}
    </Box>
  );
}

// ─── Files + Activity (kept from prior page; wiki/topics removed) ────────────

function FilesPanelMui({ prefix, tags }: { prefix: string; tags: StoreTags }) {
  const { enqueueSnackbar } = useSnackbar();
  const [path, setPath] = useState("");
  const [objects, setObjects] = useState<StoreObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setObjects(await listFiles(prefix));
    } catch {
      enqueueSnackbar("Failed to load files.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [prefix, enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  interface Entry { kind: "folder" | "file"; name: string; key: string; size?: number }
  const fullPrefix = prefix + path;
  const entries = useMemo<Entry[]>(() => {
    const folders = new Map<string, Entry>();
    const files: Entry[] = [];
    for (const o of objects) {
      if (!o.key.startsWith(fullPrefix)) continue;
      const rest = o.key.slice(fullPrefix.length);
      if (!rest || rest === ".keep") continue;
      const slash = rest.indexOf("/");
      if (slash === -1) files.push({ kind: "file", name: rest, key: o.key, size: o.size });
      else {
        const name = rest.slice(0, slash);
        if (!folders.has(name)) folders.set(name, { kind: "folder", name, key: `${fullPrefix}${name}/` });
      }
    }
    return [...[...folders.values()].sort((a, b) => a.name.localeCompare(b.name)), ...files.sort((a, b) => a.name.localeCompare(b.name))];
  }, [objects, fullPrefix]);

  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadFile(`${fullPrefix}${f.name}`, f, tags);
      enqueueSnackbar(`Uploaded ${Array.from(files).length} file(s).`, { variant: "success" });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Upload failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      label="Files"
      actions={
        <>
          <Button variant="outlined" size="small" onClick={() => {
            const name = window.prompt("New folder name")?.trim();
            if (!name) return;
            void createFolder(`${fullPrefix}${name}/`, tags).then(load);
          }} startIcon={<Icon name="folder-plus" size={15} />}>
            New folder
          </Button>
          <Button variant="contained" size="small" disabled={busy} onClick={() => fileInput.current?.click()} startIcon={<Icon name="upload" size={15} />}>
            Upload
          </Button>
          <Box component="input" ref={fileInput} type="file" multiple hidden onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.target.files && void upload(e.target.files)} />
        </>
      }
    >
      {loading ? (
        <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading files…</Box>
      ) : entries.length === 0 ? (
        <EmptyState icon="folder" title="Empty folder">Drop a file below, or create a folder.</EmptyState>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {entries.map((e) => (
            <Box
              key={e.key}
              onClick={() => (e.kind === "folder" ? setPath(e.key.slice(prefix.length)) : void presignFile(e.key).then((u) => window.open(u, "_blank", "noopener")))}
              sx={{ display: "flex", alignItems: "center", gap: "11px", padding: "14px 16px", border: `1px solid ${c.borderLight}`, borderRadius: "8px", cursor: "pointer", "&:hover": { background: c.panelBg3 } }}
            >
              <Icon name={e.kind === "folder" ? "folder" : "book"} size={18} />
              <Box sx={{ flex: 1 }}>{e.name}</Box>
              {e.size != null && <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint }}>{fmtBytes(e.size)}</Box>}
            </Box>
          ))}
        </Box>
      )}
      <Box
        onDragOver={(ev) => { ev.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(ev) => { ev.preventDefault(); setDragOver(false); if (ev.dataTransfer.files.length) void upload(ev.dataTransfer.files); }}
        sx={{ mt: "14px", border: `1.5px dashed ${dragOver ? c.teal : c.dropBorder}`, borderRadius: "10px", padding: "34px", textAlign: "center", fontSize: 13.5, color: dragOver ? c.tealInk : c.faint, background: dragOver ? c.tealTint2 : "transparent" }}
      >
        Drop files to upload
      </Box>
    </SectionCard>
  );
}

function ActivityFeedMui({ load, emptyHint }: { load: () => Promise<Thought[]>; emptyHint?: ReactNode }) {
  const { enqueueSnackbar } = useSnackbar();
  const [atoms, setAtoms] = useState<Thought[] | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((a) => { if (!cancelled) setAtoms(a); })
      .catch((e) => { if (!cancelled) { enqueueSnackbar(e instanceof Error ? e.message : "Failed to load activity.", { variant: "error" }); setAtoms([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load, enqueueSnackbar]);

  if (loading) return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading activity…</Box>;
  if (!atoms || atoms.length === 0) return <EmptyState icon="activity" title="No activity yet">{emptyHint ?? "Nothing has been tagged here yet."}</EmptyState>;

  return (
    <SectionCard padding={0}>
      {atoms.slice(0, 40).map((t, i) => (
        <Box key={t.id ?? i} sx={{ display: "flex", gap: 1.5, p: "13px 20px", borderTop: i === 0 ? "none" : `1px solid ${c.borderLight}` }}>
          <Icon name="book" size={16} style={{ color: c.faint, marginTop: 2 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 13.5, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.content.split("\n", 1)[0].slice(0, 120)}
            </Box>
            <Box sx={{ fontSize: 12, color: c.muted2 }}>{t.topics.slice(0, 2).join(" · ") || t.type}</Box>
          </Box>
        </Box>
      ))}
    </SectionCard>
  );
}
